'use client'

import { useEffect, useMemo, useState } from 'react'

import { FileTextIcon, MaximizeIcon } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { track } from '@/lib/analytics/track'
import { cn } from '@/lib/utils'

import { ReportPreviewThumb } from './report-preview-thumb'
import { buildAdsAuditReportHtml } from './sample-audit-report-html'
import {
  DEFAULT_REPORT_KEY,
  SAMPLE_REPORTS,
  type SampleReport,
} from './sample-audit-reports'

/**
 * The sample ads-audit artifact — mirrors how the product attaches a generated
 * report in chat: a clickable card that previews the rendered report and opens
 * the FULL version in a scrollable dialog. An industry switcher lets visitors
 * browse a report for their vertical (ecommerce / app / game / SaaS / …) before
 * signing up.
 *
 * Each report is rendered in Soku's editorial report style via an isolated
 * <iframe srcDoc> (see ./sample-audit-report-html, which maps the structured
 * audit onto the shared competitor-report renderer) so it matches the polished
 * standalone HTML the product actually generates. The preview is the full
 * document scaled to fit the card (see PreviewThumb) — the same treatment as
 * /free-competitor-analysis.
 */
export function SampleReportArtifact({ className }: { className?: string }) {
  const [activeKey, setActiveKey] = useState(DEFAULT_REPORT_KEY)
  const [open, setOpen] = useState(false)

  // Build each report's HTML once; switching industries just swaps srcDoc.
  const htmlByKey = useMemo(() => {
    const map: Record<string, string> = {}
    for (const r of SAMPLE_REPORTS) map[r.key] = buildAdsAuditReportHtml(r)
    return map
  }, [])

  // The full report renders in an iframe; once focus moves inside it, the
  // dialog's own Escape handler stops firing. The iframe forwards Escape via
  // postMessage (see ./sample-report-html) so we can close it here.
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (
        event.data?.source === 'soku-report' &&
        event.data?.type === 'escape'
      )
        setOpen(false)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  const report =
    SAMPLE_REPORTS.find(r => r.key === activeKey) ?? SAMPLE_REPORTS[0]

  return (
    <div className={cn('w-full', className)}>
      <Dialog
        open={open}
        onOpenChange={next => {
          setOpen(next)
          if (next)
            track('free_ads_audit_sample_open', { industry: report.key })
        }}
      >
        <div className="group bg-background relative block w-full overflow-hidden rounded-2xl border shadow-2xl transition hover:shadow-[0_30px_80px_rgba(0,0,0,0.35)]">
          {/* Scaled, non-interactive thumbnail of the rendered report — the
              full document shrunk to fit so the masthead, stat band, and first
              table/chart all read at a glance (a clip + fade sell "there's
              more"). */}
          <ReportPreviewThumb
            key={report.key}
            srcDoc={htmlByKey[report.key]}
            title={`${report.brand} ads audit`}
          />

          {/* Full-card click target opens the dialog (layered over the iframe
              so the preview itself stays inert). */}
          <DialogTrigger asChild>
            <button
              type="button"
              aria-label={`View full report — ${report.brand} ads audit`}
              className="focus-visible:ring-primary absolute inset-0 z-10 cursor-pointer rounded-2xl focus:outline-none focus-visible:ring-2"
            >
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition group-hover:opacity-100">
                <span className="bg-foreground/85 text-background inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium shadow-lg">
                  <MaximizeIcon className="size-3.5" /> View full report
                </span>
              </span>
            </button>
          </DialogTrigger>

          {/* Artifact footer (filename + size), like the in-product card. */}
          <div className="relative z-20 flex items-center gap-2 border-t px-4 py-3">
            <FileTextIcon className="text-primary size-4 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{report.fileName}</p>
              <p className="text-muted-foreground text-xs">{report.fileSize}</p>
            </div>
            <span className="bg-muted text-muted-foreground ml-auto rounded-full px-2.5 py-1 text-[11px] font-medium">
              Sample
            </span>
          </div>
        </div>

        <DialogContent className="h-[90vh] w-[1000px] max-w-[96vw] overflow-hidden p-0">
          <DialogTitle className="sr-only">
            Sample {report.industry} ads account audit report
          </DialogTitle>
          <div className="flex h-full flex-col">
            <iframe
              key={report.key}
              title={`${report.brand} ads account audit`}
              srcDoc={htmlByKey[report.key]}
              sandbox="allow-scripts allow-same-origin"
              className="min-h-0 flex-1 border-0"
            />
            <div className="bg-background border-t px-4 py-3 sm:px-6">
              <IndustryTabs activeKey={activeKey} onSelect={setActiveKey} />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Industry switcher sits under the report card. */}
      <IndustryTabs
        className="mt-3"
        activeKey={activeKey}
        onSelect={key => {
          setActiveKey(key)
          track('free_ads_audit_sample_industry', { industry: key })
        }}
      />
    </div>
  )
}

function IndustryTabs({
  activeKey,
  onSelect,
  className,
}: {
  activeKey: string
  onSelect: (key: string) => void
  className?: string
}) {
  return (
    <div className={cn('flex flex-wrap gap-1.5', className)}>
      {SAMPLE_REPORTS.map((r: SampleReport) => {
        const active = r.key === activeKey
        return (
          <button
            key={r.key}
            type="button"
            onClick={() => onSelect(r.key)}
            className={cn(
              'whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition',
              active
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-background text-muted-foreground hover:text-foreground',
            )}
          >
            {r.industry}
          </button>
        )
      })}
    </div>
  )
}
